/**
 * Dashboard LexCounsel answers — built from workspace matter data, not statute research.
 */

import { format, parseISO, isValid } from 'date-fns';
import type { LexCounselWorkspaceSnapshot } from '../store/lexCounselStore';

export interface LexCounselDashboardHearing {
  caseId: string;
  title: string;
  date: string;
  daysUntil: number;
}

export interface LexCounselDashboardReminder {
  id: string;
  message: string;
  caseId?: string;
  caseTitle?: string;
}

export interface LexCounselDashboardMatter {
  caseId: string;
  title: string;
  nextHearingDate?: string;
  stage?: string;
}

export interface LexCounselDashboardDraft {
  draftId: string;
  title: string;
  caseTitle?: string;
  updatedAt?: string;
}

export interface LexCounselDashboardBrief {
  hearings: LexCounselDashboardHearing[];
  pendingReminders: number;
  openDrafts: number;
  totalCases?: number;
  firmName?: string;
  workspaceType?: string;
  reminders?: LexCounselDashboardReminder[];
  activeMatters?: LexCounselDashboardMatter[];
  openDraftItems?: LexCounselDashboardDraft[];
}

function formatHearingDate(iso: string): string {
  try {
    const d = parseISO(iso);
    return isValid(d) ? format(d, 'EEE, d MMM yyyy') : iso;
  } catch {
    return iso;
  }
}

function formatStructuredHtml(title: string, sections: Array<{ heading: string; body: string }>): string {
  const parts = sections
    .filter((s) => s.body.trim())
    .map((s) => `<h4>${s.heading}</h4><p>${s.body.replace(/\n/g, '<br/>')}</p>`);
  return `<p><strong>${title}</strong></p>${parts.join('')}`;
}

function hearingLine(h: LexCounselDashboardHearing): string {
  const when =
    h.daysUntil === 0
      ? 'today'
      : h.daysUntil === 1
        ? 'tomorrow'
        : `in ${h.daysUntil} days (${formatHearingDate(h.date)})`;
  return `${h.title} — hearing ${when}`;
}

export function isDashboardBriefingQuestion(question: string): boolean {
  const q = question.toLowerCase();
  return (
    /priorit|today|focus|active matter|what should i|this week|upcoming hearing|hearing|reminder|draft|pending|deadline|continue working|action required/.test(
      q,
    ) || /what can you help|what can lex/.test(q)
  );
}

export function answerDashboardQuestion(
  question: string,
  brief: LexCounselDashboardBrief | undefined,
): { text: string; structured: Record<string, unknown> } {
  const q = question.toLowerCase();

  if (/what can you help|what can lex/.test(q)) {
    return {
      text: formatStructuredHtml('LexCounsel on your dashboard', [
        {
          heading: 'From here you can',
          body: [
            'See today’s priorities across hearings, reminders, and drafts',
            'Review upcoming hearings this week',
            'Open a case to ask about parties, dates, and proceedings',
            'Open a draft to redraft clauses or review your document',
          ].join('\n'),
        },
      ]),
      structured: { type: 'dashboard_help' },
    };
  }

  if (!brief) {
    return {
      text: formatStructuredHtml('Workspace brief', [
        {
          heading: 'Data not loaded',
          body: 'Stay on the dashboard for a moment so your matter list can load, then try again.',
        },
      ]),
      structured: { type: 'dashboard_brief', empty: true },
    };
  }

  if (/hearing|upcoming|this week/.test(q) && !/priorit|today|focus|what should/.test(q)) {
    const lines = brief.hearings.length
      ? brief.hearings.map(hearingLine).join('\n')
      : 'No hearings in the next 14 days on your active matters.';
    return {
      text: formatStructuredHtml('Upcoming hearings', [
        { heading: 'Next 14 days', body: lines },
      ]),
      structured: { type: 'dashboard_hearings', hearings: brief.hearings },
    };
  }

  const today = brief.hearings.filter((h) => h.daysUntil === 0);
  const thisWeek = brief.hearings.filter((h) => h.daysUntil > 0 && h.daysUntil <= 7);
  const reminderLines =
    brief.reminders
      ?.slice(0, 5)
      .map((r) => (r.caseTitle ? `${r.message} (${r.caseTitle})` : r.message))
      .join('\n') ||
    (brief.pendingReminders > 0
      ? `${brief.pendingReminders} item(s) need attention — see Action Required on the dashboard.`
      : '');

  const matterLines =
    brief.activeMatters
      ?.slice(0, 6)
      .map((m) => {
        const parts = [m.title];
        if (m.nextHearingDate) parts.push(`next hearing ${formatHearingDate(m.nextHearingDate)}`);
        if (m.stage) parts.push(m.stage);
        return parts.join(' · ');
      })
      .join('\n') || '';

  const priorityBullets: string[] = [];
  if (today.length) {
    priorityBullets.push(`Prepare for ${today.length} hearing${today.length === 1 ? '' : 's'} today:\n${today.map(hearingLine).join('\n')}`);
  }
  if (thisWeek.length) {
    priorityBullets.push(
      `${thisWeek.length} hearing${thisWeek.length === 1 ? '' : 's'} this week:\n${thisWeek.map(hearingLine).join('\n')}`,
    );
  }
  if (brief.pendingReminders > 0) {
    priorityBullets.push(
      `Resolve ${brief.pendingReminders} pending reminder${brief.pendingReminders === 1 ? '' : 's'}${reminderLines ? `:\n${reminderLines}` : ''}`,
    );
  }
  if (brief.openDrafts > 0) {
    priorityBullets.push(
      `Continue ${brief.openDrafts} draft${brief.openDrafts === 1 ? '' : 's'} in progress (see Continue Working on the dashboard)`,
    );
  }
  if (!priorityBullets.length) {
    priorityBullets.push('No urgent hearings or reminders in the next two weeks. Review active matters or advance open drafts.');
  }

  return {
    text: formatStructuredHtml('Today’s priorities', [
      { heading: 'Recommended focus', body: priorityBullets.join('\n\n') },
      ...(matterLines
        ? [{ heading: 'Active matters', body: matterLines }]
        : []),
    ]),
    structured: {
      type: 'dashboard_priorities',
      hearings: brief.hearings,
      pendingReminders: brief.pendingReminders,
      openDrafts: brief.openDrafts,
    },
  };
}

export function workspaceHasDashboardBrief(
  workspace: LexCounselWorkspaceSnapshot,
): workspace is LexCounselWorkspaceSnapshot & { dashboardBrief?: LexCounselDashboardBrief } {
  return workspace.screen === 'dashboard';
}
